import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "./api";
import { useAuth } from "./auth";

const FavoritesContext = createContext(null);

/**
 * FavoritesProvider — holds a Set of the logged-in user's saved MLS listing_keys
 * and exposes toggle/isSaved helpers. Anonymous users get a no-op provider
 * that returns isSaved=false and, on toggle, opens the Emergent Auth login.
 */
export function FavoritesProvider({ children }) {
  const { user, login } = useAuth();
  const [keys, setKeys] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setKeys(new Set());
      setLoaded(true);
      return;
    }
    try {
      const { data } = await api.get("/favorites/keys", { withCredentials: true });
      setKeys(new Set(data.listing_keys || []));
    } catch {
      setKeys(new Set());
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isSaved = useCallback((listingKey) => keys.has(listingKey), [keys]);

  const toggle = useCallback(
    async (listingKey, meta = {}) => {
      if (!user) {
        toast("Sign in to save this listing", {
          description: "We'll bring you right back to your saved homes.",
          action: { label: "Sign in", onClick: () => login() },
        });
        return;
      }
      const already = keys.has(listingKey);
      // Optimistic update
      setKeys((prev) => {
        const next = new Set(prev);
        if (already) next.delete(listingKey);
        else next.add(listingKey);
        return next;
      });
      try {
        if (already) {
          await api.delete(`/favorites/${listingKey}`, { withCredentials: true });
          toast("Removed from your saved homes");
        } else {
          await api.post(
            `/favorites/${listingKey}`,
            {},
            { withCredentials: true }
          );
          toast("Saved to your listings", {
            description: meta.address || "View them anytime under Saved Homes.",
          });
        }
      } catch (e) {
        // Revert on failure
        setKeys((prev) => {
          const next = new Set(prev);
          if (already) next.add(listingKey);
          else next.delete(listingKey);
          return next;
        });
        toast.error("Could not update saved listings. Please try again.");
      }
    },
    [user, keys, login]
  );

  return (
    <FavoritesContext.Provider
      value={{ keys, isSaved, toggle, refresh, loaded }}
    >
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used inside FavoritesProvider");
  return ctx;
}
